import React, { useMemo } from 'react';

import { getStripeKeyPrefix, type useCreditCardOptions } from '@order/integrations/stripe';

import {
  useGiftCardCheckoutFormState,
  type GiftCardCheckoutFormFieldRules,
  type GiftCardCheckoutFormReturn,
} from '../../form';
import { GiftCardCheckoutFormContent } from './components';
import { GiftCardCheckoutPaymentProvider } from './sections';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardCheckoutFormV2 = (props: GiftCardCheckoutFormProps) => {
  const { form, submitForm, purchaseAttempt, sectionTitles, rules, incrementPurchaseAttempt } =
    props;

  // ─── Form State ────────────────────────────────────────────────────────────

  const { amount, isSubmitting, canSubmit, requestFormSubmission } = useGiftCardCheckoutFormState({
    form,
    submitForm,
  });

  const paymentMethods = useMemo(() => {
    return mapPaymentMethods(props.paymentMethods);
  }, [props.paymentMethods]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <GiftCardCheckoutPaymentProvider form={form}>
      <GiftCardCheckoutFormContent
        key={getStripeKeyPrefix(purchaseAttempt)}
        amount={amount}
        sectionTitles={sectionTitles}
        paymentMethods={paymentMethods}
        form={form}
        rules={rules}
        canSubmit={canSubmit}
        isSubmitting={isSubmitting}
        failPayment={incrementPurchaseAttempt}
        requestFormSubmission={requestFormSubmission}
      />
    </GiftCardCheckoutPaymentProvider>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const mapPaymentMethods = (
  paymentMethods: ReturnType<typeof useCreditCardOptions>['creditCardOptions'],
) => {
  return paymentMethods.map((paymentMethod) => ({
    id: paymentMethod.value,
    description: paymentMethod.label,
    nickname: paymentMethod.label,
    cardType: paymentMethod.cardType as any,
    isDefault: paymentMethod.isDefault,
  }));
};

// ─── Types ───────────────────────────────────────────────────────────────────

type GiftCardCheckoutFormProps = {
  purchaseAttempt: number;
  incrementPurchaseAttempt: () => void;
  form: GiftCardCheckoutFormReturn;
  rules: GiftCardCheckoutFormFieldRules;
  paymentMethods: ReturnType<typeof useCreditCardOptions>['creditCardOptions'];
  submitForm: () => Promise<void>;
  sectionTitles?: {
    amount: string;
    details: string;
    deliveryDate: string;
    deliveryMethod: string;
    recipientEmail: string;
    paymentMethod: string;
  };
};
